/* eslint-disable react/prop-types */
import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import SortableBoxList from 'modules/core/components/SortableBoxList'
import i18n from 'modules/core/lib/i18n'

import type SurveyForm from 'modules/surveys/store/models/Form'
import type QuestionConfig from 'modules/surveys/store/models/QuestionConfig'
import type Survey from 'modules/surveys/store/models/v2/Survey'

import Item from './Item'

type ItemProps = React.ComponentProps<typeof Item>

interface Props {
  form: SurveyForm | Survey
  questionConfigs: QuestionConfig[]
  archived?: boolean
  updateQuestionConfig: ItemProps['updateQuestionConfig']
  createQuestionConfigFromDuplicate: ItemProps['createQuestionConfigFromDuplicate']
  toggleArchivedQuestionConfig: ItemProps['toggleArchivedQuestionConfig']
  reorderQuestionConfig: (config: { id: string; newPosition: number }) => void
  deleteQuestionConfig: ItemProps['deleteQuestionConfig']
}

function List({
  form,
  questionConfigs,
  archived = false,
  updateQuestionConfig,
  createQuestionConfigFromDuplicate,
  toggleArchivedQuestionConfig,
  reorderQuestionConfig,
  deleteQuestionConfig,
}: Props) {
  const items = questionConfigs.sort((a, b) =>
    a.attributes.position > b.attributes.position ? 1 : -1
  )

  const onSortEnd = (items: QuestionConfig[]) => {
    items.forEach((item, index) => {
      reorderQuestionConfig({
        id: item.attributes.id,
        newPosition: index,
      })
    })
  }

  const RenderedItem = ({ item }: { item: QuestionConfig }) => (
    <Item
      key={item.attributes.id}
      form={form}
      questionConfig={item}
      updateQuestionConfig={updateQuestionConfig}
      createQuestionConfigFromDuplicate={createQuestionConfigFromDuplicate}
      toggleArchivedQuestionConfig={toggleArchivedQuestionConfig}
      deleteQuestionConfig={deleteQuestionConfig}
    />
  )

  return (
    <Box>
      {archived && (
        <Box flexDirection="row" alignItems="center" paddingBottom="s24" gap="s12">
          <Text size="300" weight="medium" color="grey1000">
            {i18n.t('surveys.form.question_configs.archived_list')}
          </Text>
          <InformationTooltip
            information={i18n.t('surveys.form.question_configs.archived_list_tooltip')}
          />
        </Box>
      )}
      {
        <SortableBoxList
          items={items}
          onSortEnd={onSortEnd}
          emptyMessage={i18n.t('surveys.form.question_configs.no_question_configs')}
          itemAsKey={(item) => item.attributes.id}
          renderItem={(item) => <RenderedItem item={item} />}
        />
      }
    </Box>
  )
}

export default observer(List)
